import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args as TemplatesTransferedNotificationBaseArgs } from 'webapp/components/templates/transfered-notification/base';
import TemplatesTransferedNotificationBaseComponent from 'webapp/components/templates/transfered-notification/base';

interface Args extends TemplatesTransferedNotificationBaseArgs {
	cellsInRowCount: number;
	lowPriorityCellsInRowCount: number;
	mediumPriorityCellsInRowCount: number;
}

export default class TemplatesTransferedNotificationAccountTableRowComponent extends TemplatesTransferedNotificationBaseComponent<Args> {
	@tracked cellOneColspan = 0;

	private calculateCellOneColspanFn: (() => void) | null = null;
	cellsWithFixedColspanInRowCount = 3; // first, last column (c0, c2) and toggle

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.calculateCellOneColspan();
	}

	calculateCellOneColspan(): void {
		const breakpoints = this.getBreakpoints();
		this.cellOneColspan = this.args.cellsInRowCount - this.cellsWithFixedColspanInRowCount;

		if (window.innerWidth <= breakpoints.breakpoint_hide_low) {
			this.cellOneColspan -= this.args.lowPriorityCellsInRowCount;
		}

		if (window.innerWidth <= breakpoints.breakpoint_hide_medium) {
			this.cellOneColspan -= this.args.mediumPriorityCellsInRowCount;
		}
	}

	@action
	setupView(): void {
		// bind events on resize
		const calculateCellOneColspanFn = () => {
			debounce(this, this.calculateCellOneColspan, 100);
		};

		this.calculateCellOneColspanFn = calculateCellOneColspanFn;

		window.addEventListener('resize', this.calculateCellOneColspanFn);
	}

	@action
	teardownView(): void {
		if (this.calculateCellOneColspanFn) {
			window.removeEventListener('resize', this.calculateCellOneColspanFn);
		}
	}

	private getBreakpoints() {
		return {
			breakpoint_hide_low: 1200,
			breakpoint_hide_medium: 768,
		};
	}
	<template>
		<tr
			class={{this.classNames}}
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			<td class="cc no-grow first"></td>
			<td class="c0 board no-grow">
				<div class="arrow-icon">
					<CommonUiIconComponent @name="ArrowDown" @size="2xl" />
				</div>
			</td>
			<td class="c1" colspan={{this.cellOneColspan}}>
				<strong>{{@model.title}}</strong>
				<small>{{{@model.shownText}}}</small>
			</td>
			<td class="button-ct">
				<div class="button-ct__wrapper">
					<a
						role="button"
						class="accept"
						{{on "click" (fn this.handleTransferedObject true)}}
					>
						<CommonUiIconComponent @name="Check" @size="sm" />
						Accept
					</a>
					<a
						role="button"
						class="reject"
						{{on "click" (fn this.handleTransferedObject false)}}
					>
						Reject
					</a>
				</div>
			</td>
		</tr>
	</template>
}
